import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiRefreshCw, FiLoader } from 'react-icons/fi';
import api from '../services/api';
import './PendingRCsSection.css';

const PendingRCsSection = () => {
    const [pendingOrders, setPendingOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [applying, setApplying] = useState({});
    const [error, setError] = useState(null);
    const [historyOrders, setHistoryOrders] = useState([]);
    const [saldoFavorOrders, setSaldoFavorOrders] = useState([]);
    const [viewMode, setViewMode] = useState('pending'); // 'pending' | 'history' | 'saldo_favor'

    const fetchPendingRCs = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/cartera/pending-rcs');

            if (response.data.success) {
                setPendingOrders(response.data.data || []);
                setHistoryOrders(response.data.history || []);
                setSaldoFavorOrders(response.data.saldoFavor || []);
            }
        } catch (err) {
            console.error('Error fetching pending RCs:', err);
            setError('Error cargando RCs pendientes');
        } finally {
            setLoading(false);
        }
    };

    const activeList = viewMode === 'pending' ? pendingOrders : viewMode === 'history' ? historyOrders : saldoFavorOrders;

    useEffect(() => {
        fetchPendingRCs();
    }, []);

    // --- MODAL & PROGRESS LOGIC ---
    const [modalOpen, setModalOpen] = useState(false);
    const [progress, setProgress] = useState(0);
    const [modalStatus, setModalStatus] = useState('idle'); // idle, processing, success, error
    const [modalMessage, setModalMessage] = useState('');
    const [currentOrderId, setCurrentOrderId] = useState(null);

    const handleApplyRC = async (orderId) => {
        setModalOpen(true);
        setModalStatus('processing');
        setProgress(10);
        setModalMessage('Iniciando creación de Recibo de Caja...');
        setCurrentOrderId(orderId);
        setApplying(prev => ({ ...prev, [orderId]: true }));

        // Simulate progress
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 90) return 90;
                return prev + 10;
            });
        }, 300);

        try {
            const response = await api.post(`/cartera/apply-rc/${orderId}`);
            clearInterval(interval);
            setProgress(100);

            if (response.data.success) {
                setModalStatus('success');
                const rcNames = response.data.createdRCs ? response.data.createdRCs.map(r => r.name).join(', ') : '';
                setModalMessage(`✅ RC creado exitosamente: ${rcNames}`);

                // Auto-close after 2 seconds
                setTimeout(() => {
                    setModalOpen(false);
                    // Remove from list optimistically
                    setPendingOrders(prev => prev.filter(o => o.id !== orderId));
                    // Manually refresh to get history update
                    fetchPendingRCs();
                }, 2000);
            }
        } catch (err) {
            clearInterval(interval);
            setProgress(100);
            setModalStatus('error');
            console.error('Error applying RC:', err);

            const is503 = err.response?.data?.siigo_unavailable;
            const message = is503
                ? '⚠️ Siigo aún no está disponible. Intenta más tarde.'
                : `❌ Error: ${err.response?.data?.message || err.message}`;

            setModalMessage(message);
        } finally {
            setApplying(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const handleApproveSaldoFavor = async (orderId) => {
        if (!window.confirm('¿Aprobar este pedido como pagado con saldo a favor?')) return;
        setApplying(prev => ({ ...prev, [orderId]: true }));
        try {
            const response = await api.post(`/cartera/approve-saldo-favor/${orderId}`);
            if (response.data.success) {
                setPendingOrders(prev => prev.filter(o => o.id !== orderId));
                fetchPendingRCs();
            }
        } catch (err) {
            alert('Error: ' + (err.response?.data?.message || err.message));
        } finally {
            setApplying(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const closeModal = () => {
        if (modalStatus === 'processing') return; // Prevent closing while processing
        setModalOpen(false);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-CO');
    };

    const getManualRCInfo = (order) => {
        if (!order.notes) return null;
        const match = order.notes.match(/\[AUTO-RC:\s*([^|]+)\|([^\]]+)\]/);
        if (match) return { name: match[1], id: match[2] };
        return null;
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative">

            {/* PROGRESS MODAL */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800">
                            {modalStatus === 'processing' ? 'Procesando...' :
                                modalStatus === 'success' ? '¡Éxito!' : 'Error'}
                        </h3>

                        <div className="w-full bg-gray-200 rounded-full h-4 mb-4 overflow-hidden">
                            <div
                                className={`h-4 rounded-full transition-all duration-300 ${modalStatus === 'error' ? 'bg-red-500' :
                                    modalStatus === 'success' ? 'bg-green-500' : 'bg-blue-600'
                                    }`}
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>

                        <p className={`text-sm mb-6 ${modalStatus === 'error' ? 'text-red-600 font-medium' :
                            modalStatus === 'success' ? 'text-green-600 font-medium' : 'text-gray-600'
                            }`}>
                            {modalMessage}
                        </p>

                        <div className="flex justify-end">
                            {modalStatus !== 'processing' && (
                                <button
                                    onClick={closeModal}
                                    className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition"
                                >
                                    Cerrar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <FiAlertTriangle className={viewMode === 'pending' ? "text-orange-500" : "text-gray-400"} />
                        Gestión de RCs
                    </h2>

                    <div className="flex bg-white rounded-lg border border-gray-300 p-0.5 shadow-sm">
                        <button
                            onClick={() => setViewMode('pending')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'pending'
                                ? 'bg-orange-100 text-orange-800'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Pendientes ({pendingOrders.length})
                        </button>
                        <button
                            onClick={() => setViewMode('saldo_favor')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'saldo_favor'
                                ? 'bg-green-100 text-green-800'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Saldo a Favor ({saldoFavorOrders.length})
                        </button>
                        <button
                            onClick={() => setViewMode('history')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'history'
                                ? 'bg-blue-100 text-blue-800'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Historial Manual ({historyOrders.length})
                        </button>
                    </div>
                </div>

                <button
                    onClick={fetchPendingRCs}
                    className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-white transition-colors"
                    title="Actualizar listado"
                >
                    <FiRefreshCw />
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <FiRefreshCw className="animate-spin text-3xl text-blue-500 mr-2" />
                    <span className="text-gray-500">Cargando datos...</span>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 m-4 rounded relative" role="alert">
                    <strong className="font-bold">Error:</strong>
                    <span className="block sm:inline"> {error}</span>
                    <button onClick={fetchPendingRCs} className="mt-2 text-red-700 underline">Reintentar</button>
                </div>
            ) : activeList.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 m-4 text-center">
                    <FiCheckCircle className="mx-auto text-4xl text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-800">
                        {viewMode === 'pending' ? 'Todo al día' : viewMode === 'saldo_favor' ? 'Sin aprobaciones' : 'Sin historial reciente'}
                    </h3>
                    <p className="text-gray-600 mt-2">
                        {viewMode === 'pending'
                            ? 'No se encontraron pedidos pendientes de RC.'
                            : viewMode === 'saldo_favor'
                                ? 'No hay pedidos aprobados con saldo a favor.'
                                : 'No hay registros de correcciones manuales recientes.'}
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pedido</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detalles</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Método Pago</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pagado Local</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {viewMode === 'pending' ? 'Razón' : viewMode === 'saldo_favor' ? 'Estado' : 'Estado'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {activeList.map((order) => {
                                const isContingency = order.pending_type === 'contingency';
                                const manualInfo = viewMode === 'history' ? getManualRCInfo(order) : null;

                                return (
                                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-gray-900">{order.order_number}</div>
                                            <div className="text-xs text-gray-500">{formatDate(order.created_at)}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{order.customer_name}</div>
                                            <div className="text-xs text-gray-500">{order.customer_identification}</div>
                                            <div className="text-xs text-gray-400 mt-0.5">{order.delivery_method}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                {order.local_payment_details ? (
                                                    JSON.parse(JSON.stringify(order.local_payment_details)).map((pay, idx) => (
                                                        <span key={idx} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-50 text-blue-800 border border-blue-200 justify-between min-w-[120px]">
                                                            <span>{pay.method}</span>
                                                            <span className="ml-2">{formatCurrency(pay.amount)}</span>
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                                        {order.payment_method || 'Sin definir'}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {formatCurrency(order.paid_amount > 0 ? order.paid_amount : order.total_amount)}
                                            </div>
                                            {order.local_payments_count > 0 && (
                                                <span className="text-xs inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-800">
                                                    {order.local_payments_count} pagos en caja
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {viewMode === 'pending' ? (
                                                isContingency ? (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                        ⚠️ Contingencia Siigo
                                                    </span>
                                                ) : (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                                                        🔍 Falta RC (Tiene Pagos)
                                                    </span>
                                                )
                                            ) : viewMode === 'saldo_favor' ? (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                    ✅ Aprobado
                                                </span>
                                            ) : (
                                                <span className="flex flex-col gap-1">
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                        ✅ Corregido Manualmente
                                                    </span>
                                                    {manualInfo && (
                                                        <span className="text-xs text-gray-500 font-mono">
                                                            {manualInfo.name}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {viewMode === 'pending' ? (
                                                <div className="flex gap-2">
                                                    {/* Check if order has saldo_a_favor payments */}
                                                    {(() => {
                                                        let hasSaldoFavor = false;
                                                        try {
                                                            const details = order.local_payment_details;
                                                            if (details) {
                                                                const parsed = typeof details === 'string' ? JSON.parse(details) : details;
                                                                hasSaldoFavor = Array.isArray(parsed) && parsed.some(p => p.method === 'saldo_a_favor');
                                                            }
                                                        } catch (e) { }
                                                        if (!hasSaldoFavor && (order.payment_method || '').includes('saldo_a_favor')) hasSaldoFavor = true;

                                                        return hasSaldoFavor ? (
                                                            <button
                                                                onClick={() => handleApproveSaldoFavor(order.id)}
                                                                disabled={applying[order.id]}
                                                                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 
                                                                    ${applying[order.id] ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                                                            >
                                                                {applying[order.id] ? 'Procesando...' : '✓ Aprobar'}
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => handleApplyRC(order.id)}
                                                                disabled={applying[order.id]}
                                                                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                                                                    ${applying[order.id] ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                                                            >
                                                                {applying[order.id] ? (
                                                                    <>
                                                                        <FiLoader className="animate-spin mr-2" />
                                                                        Procesando...
                                                                    </>
                                                                ) : (
                                                                    'Crear RC en Siigo'
                                                                )}
                                                            </button>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">
                                                    Acción completada
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PendingRCsSection;

